<template>
    <section v-if="isCommunityDisabled" class="qr-home qr-home--disabled" data-test="qr-home-community-disabled">
        <Icon name="qr-community-disabled" class="qr-home__disabled-art" />
        <h1 class="qr-home__disabled-title">{{ $t('qr.home.community_disabled') }}</h1>
    </section>

    <section v-else class="qr-home" data-test="qr-home">
        <header class="qr-home__header">
            <div class="qr-home__brand">
                <span class="qr-home__logo">
                    <Icon name="qr-building" class="w-7 h-7" />
                </span>
                <h1 v-if="isVerified" class="qr-home__title" data-test="qr-home-title">{{ $t('qr.home.welcome', { name: communityName }) }}</h1>
                <h1 v-else-if="isError" class="qr-home__title" data-test="qr-home-title">{{ $t('qr.home.load_error') }}</h1>
                <h1 v-else class="qr-home__title" data-test="qr-home-title">{{ $t('qr.home.loading') }}</h1>
            </div>
            <p v-if="isVerified && description" class="qr-home__description" data-test="qr-home-description">{{ description }}</p>
            <div v-if="isVerified" class="qr-home__details">
                <p v-if="address" class="qr-home__line qr-home__line--address" data-test="qr-home-address">
                    <Icon name="qr-pin" class="qr-home__line-icon w-5 h-5" />
                    <span>{{ address }}</span>
                </p>
                <p v-if="intercomName" class="qr-home__line qr-home__line--intercom" data-test="qr-home-intercom">
                    <Icon name="qr-door" class="qr-home__line-icon w-5 h-5" />
                    <span>{{ intercomName }}</span>
                </p>
            </div>
            <button
                v-if="isError"
                class="btn btn-primary qr-home__retry"
                type="button"
                data-test="qr-home-retry"
                @click="retry"
            >
                {{ $t('qr.home.retry') }}
            </button>
        </header>

        <div v-if="isVerified" class="qr-home__cards">
            <h2 class="qr-home__subtitle" data-test="qr-home-subtitle">{{ $t('qr.home.who_to_see') }}</h2>
            <router-link
                v-if="enabledForLocation"
                :to="{ name: QR_ROUTES.LOCATION, params: { entranceId: route.params.entranceId } }"
                class="qr-card"
                data-test="qr-home-resident"
            >
                <span class="qr-card__badge">
                    <Icon name="qr-users" class="w-6 h-6" />
                </span>
                <span class="qr-card__body">
                    <span class="qr-card__title">{{ $t('qr.home.call_resident') }}</span>
                    <span class="qr-card__text">{{ $t('qr.home.call_resident_notice') }}</span>
                </span>
                <Icon name="qr-chevron-right" class="qr-card__trailing w-6 h-6" />
            </router-link>
            <div
                v-else
                class="qr-card qr-card--disabled"
                aria-disabled="true"
                data-test="qr-home-resident-disabled"
            >
                <span class="qr-card__badge">
                    <Icon name="qr-users" class="w-6 h-6" />
                </span>
                <span class="qr-card__body">
                    <span class="qr-card__title">{{ $t('qr.home.call_resident_unavailable') }}</span>
                    <span class="qr-card__text">{{ $t('qr.home.call_resident_unavailable_notice') }}</span>
                </span>
                <Icon name="qr-disabled" class="qr-card__trailing w-6 h-6" />
            </div>

            <a
                v-for="(office, index) in offices"
                :key="`${office.name}-${office.phoneNumber}-${index}`"
                :href="`tel:${office.phoneTel || office.phoneNumber}`"
                class="qr-card"
                :data-test="`qr-home-office-${index}`"
            >
                <span class="qr-card__body">
                    <span class="qr-card__title">{{ office.name }}</span>
                    <span class="qr-card__text">{{ office.description }}</span>
                    <span class="qr-card__phone">{{ office.phoneNumber }}</span>
                </span>
                <Icon name="qr-phone" class="qr-card__trailing w-6 h-6" />
            </a>
        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import Icon from '@/components/ui/Icon';
import { QR_ENTRANCE_STATUS, QR_ROUTES } from '../../constants/qr';

const route = useRoute();
const store = useStore();

const communityName = computed(() => store.getters['qr/communityName']);
const intercomName = computed(() => store.getters['qr/intercomName']);
const description = computed(() => store.getters['qr/description']);
const address = computed(() => store.getters['qr/address']);
const isVerified = computed(() => store.getters['qr/isVerified']);
const isCommunityDisabled = computed(() => isVerified.value && !store.getters['qr/enabledForCommunity']);
const enabledForLocation = computed(() => store.getters['qr/enabledForLocation']);
const isError = computed(() => store.getters['qr/entranceStatus'] === QR_ENTRANCE_STATUS.ERROR);
const offices = computed(() => store.getters['qr/offices']);

function retry() {
    store.dispatch('qr/loadEntrance', route.params.entranceId);
}
</script>

<style scoped>
.qr-home {
    /* Cancel ContentArea's page-paddings so the header spans edge to edge */
    margin: 0 -1.5rem;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    color: #101010;
}

.qr-home::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: -1;
    background: #f2f2f2;
}

.qr-home__header {
    padding: 24px 20px 20px;
    background: #fff;
}

.qr-home__brand {
    display: flex;
    align-items: flex-start;
    gap: 20px;
}

.qr-home__logo {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #8662bf;
    color: #fff;
}

.qr-home__title {
    font-size: 24px;
    font-weight: 700;
    line-height: 28px;
    overflow-wrap: anywhere;
}

.qr-home__details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 12px 0 0 76px;
}

.qr-home__line {
    display: flex;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 300;
    color: #696969;
    overflow-wrap: anywhere;
}

.qr-home__line--intercom {
    font-size: 16px;
    font-weight: 500;
    color: #8a5ed5;
}

.qr-home__line--address {
    font-size: 16px;
    font-weight: 500;
    color: #222;
}

.qr-home__line-icon {
    flex-shrink: 0;
}

.qr-home__line-icon :deep(svg) {
    width: 20px;
    height: 20px;
}

.qr-home__description {
    margin: 12px 0 0 76px;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 300;
    color: #696969;
    overflow-wrap: anywhere;
}

.qr-home--disabled {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 48px 32px calc(48px + env(safe-area-inset-bottom));
    background: #fff;
    text-align: center;
}

.qr-home--disabled::before {
    background: #fff;
}

.qr-home__disabled-art {
    width: 214px;
    max-width: 100%;
    margin-bottom: 32px;
}

.qr-home__disabled-title {
    max-width: 320px;
    font-size: 17px;
    font-weight: 700;
    line-height: 22px;
    text-transform: uppercase;
}

.qr-home__retry {
    width: 100%;
    margin-top: 16px;
}

.qr-home__subtitle {
    margin-bottom: -4px;
    font-size: 17px;
    font-weight: 700;
    line-height: 22px;
}

.qr-home__cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
}

.qr-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px 24px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    color: inherit;
    text-decoration: none;
}

.qr-card:focus-visible {
    outline: 2px solid #8662bf;
    outline-offset: 2px;
}

.qr-card__badge {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f4ecff;
    color: #8662bf;
}

.qr-card__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
}

.qr-card__title {
    margin-bottom: 4px;
    font-size: 17px;
    font-weight: 700;
    text-transform: uppercase;
}

.qr-card__text {
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 19px;
    font-weight: 300;
    color: #696969;
}

.qr-card__phone {
    margin-top: 14px;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 300;
    color: #696969;
}

.qr-card__trailing {
    flex-shrink: 0;
    color: #8a8a8a;
}

.qr-card--disabled {
    background: #fafafa;
    cursor: not-allowed;
}

.qr-card--disabled .qr-card__badge {
    opacity: 0.6;
}

.qr-card--disabled .qr-card__title {
    color: #7a7a7a;
}
</style>
